import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SET_PAGINATION_COUNT } from 'constants/dataSets';
import { KEY_PERMISSIONS, MATERIALITY_ASSESSMENT_STATUS, TASK_MANAGEMENT_PERMISSIONS } from 'constants/general';
import { SECTION_TYPE, STATUS_SECTIONS } from 'constants/materialAssessment';
import useNavigateStakeholders from 'hooks/useNavigateStakeholders';
import usePillarModulePermission from 'hooks/usePillarModulePermission';
import i18n from 'i18n';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { useUpdateSectionMAMutation } from 'store/slices/layoutsMA/apis/materialAssessmentApi';
import {
  useDeleteVCMaterialityStakeholderMutation,
  useGetSingleMAInitiateQuery,
  useGetStakeholdersSuppliersListQuery,
  useSaveSelectedSupplierMutation,
} from 'store/slices/materialAssessment/apis/materialAssessmentApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import { useLazyGetVCSuppliersQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';

import MaterialityAssessmentSupplierView from './MaterialityAssessmentSupplierView';

import type ISupplierItem from 'store/slices/dataSets/interfaces/ISupplierItem';
import type ISelectedSuppliers from 'store/slices/materialAssessment/interfaces/ISelectedSuppliers';
import type IStakeholderSupplierItem from 'store/slices/materialAssessment/interfaces/IStakeholderSupplierItem';

function MaterialityAssessmentSupplier() {
  const navigate = useNavigate();
  const { recordId = '', vcmId = 0 } = useParams();
  const location = useLocation();
  const valueChainMapId = Number(vcmId);

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const { navigateToStakeholder } = useNavigateStakeholders(recordId, valueChainMapId);

  const [vcSuppliers, setVCSuppliers] = useState<ISupplierItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMoreVCSuppliers, setHasMoreVCSuppliers] = useState(false);
  const [offsetMain, setOffsetMain] = useState(0);
  const [selectedVCSuppliers, setSelectedVCSuppliers] = useState<ISelectedSuppliers[]>([]);
  const [listSuppliers, setListSuppliers] = useState<IStakeholderSupplierItem[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const { data: singleMA, isFetching: isLoadingSingleMA } = useGetSingleMAInitiateQuery({ id: recordId });
  const { data: naceCodeTreeData = { data: {} }, isLoading: isGetNaceCodeTreeRecords } = useGetNaceCodeTreeQuery();
  const { data: userData } = useUserMeQuery();

  const {
    access,
  } = usePillarModulePermission({
    pillarModuleKey: KEY_PERMISSIONS.MATERIALITY_ASSESSMENT_MANAGEMENT,
    userId: userData?.data?.id,
  });

  const isViewOnly = access === TASK_MANAGEMENT_PERMISSIONS.VIEW_ONLY
  || access === TASK_MANAGEMENT_PERMISSIONS.SIGN_OFF
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.APPROVED
  || singleMA?.data?.status === MATERIALITY_ASSESSMENT_STATUS.PENDING_REVIEW;

  const [saveSelectedSupplier] = useSaveSelectedSupplierMutation();
  const [deleteMaterialityStakeholder] = useDeleteVCMaterialityStakeholderMutation();
  const [updateSection] = useUpdateSectionMAMutation();

  const { data: suppliersList, isFetching: isFetchingSuppliersList } = useGetStakeholdersSuppliersListQuery({
    maId: recordId,
    valueChainMapId,
    offset: offsetMain,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const hasMore = suppliersList?.data?.meta.hasNext || false;

  const [getVCSuppliers] = useLazyGetVCSuppliersQuery();

  const loadMoreSuppliers = () => {
    if (suppliersList?.data.meta.hasNext) {
      setOffsetMain((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  const loadMoreVCSuppliers = () => {
    if (hasMoreVCSuppliers) {
      setOffset((prev) => prev + DATA_SET_PAGINATION_COUNT);
    }
  };

  const handleResetVCSuppliersModal = useCallback(() => {
    setOffset(0);
    setVCSuppliers([]);
    setHasMoreVCSuppliers(false);
    setSearch('');
    setDebouncedSearch('');
  }, []);

  useEffect(() => {
    if (suppliersList && suppliersList?.data?.items.length > 0) {
      setListSuppliers((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newItems = suppliersList?.data?.items
          .filter((item) => !existingIds.has(item.id))
          .map((item) => ({ ...item, custom: true }));
        return [...prev, ...newItems];
      });
    }
  }, [suppliersList]);

  useEffect(() => {
    if (!isOpenModal) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOpenModal, search]);

  useEffect(() => {
    if (!isOpenModal || valueChainMapId === 0 || search !== debouncedSearch) {
      return undefined;
    }

    const request = getVCSuppliers({ valueChainMapId, offset, keyword: debouncedSearch });

    request.unwrap().then((response) => {
      const responseData = response.data.items;

      if (offset === 0) {
        setVCSuppliers(responseData);
      } else {
        setVCSuppliers((prev) => [...prev, ...responseData]);
      }

      setHasMoreVCSuppliers(response.data.meta.hasNext || false);
    }).catch(() => {
      setHasMoreVCSuppliers(false);
    });

    return () => {
      request.abort();
    };
  }, [debouncedSearch, getVCSuppliers, isOpenModal, offset, search, valueChainMapId]);

  useEffect(() => {
    if (isOpenModal) {
      return;
    }

    handleResetVCSuppliersModal();
  }, [handleResetVCSuppliersModal, isOpenModal]);

  const handleUpdateSection = () => {
    updateSection({
      maId: recordId,
      type: SECTION_TYPE.STAKEHOLDER_SUPPLIERS,
      valueChainMapId,
      data: {
        isActive: true,
        status: listSuppliers.length > 0 ? STATUS_SECTIONS.COMPLETED : STATUS_SECTIONS.IN_PROGRESS,
      },
    });
  };

  const goToNextMaterialityAssessment = () => {
    if (isViewOnly) {
      navigateToStakeholder(location.pathname.split('/')[3]);
      return;
    }
    handleUpdateSection();
    navigateToStakeholder(location.pathname.split('/')[3]);
  };

  const goBack = () => {
    navigate(AppRoutes.materialityAssessmentStakeholders.replace(':recordId', recordId));
  };

  const addStakeholder = () => {
    if (isViewOnly) {
      return;
    }
    setIsOpenModal(true);
    setSelectedItems([]);
  };

  const updateSelectedVCSuppliers = (newSelectedItems: number[]) => {
    setSelectedVCSuppliers((prev) => {
      if (newSelectedItems.length === 0) {
        return prev.filter((item) => item.vcmId !== valueChainMapId);
      }
      const data = {
        vcmId: valueChainMapId,
        recordIds: newSelectedItems,
      };
      const exists = prev.some((item) => item.vcmId === data.vcmId);
      if (exists) {
        return prev.map((item) => (item.vcmId === data.vcmId ? data : item));
      }
      return [...prev, data];
    });
  };

  const onSelectedItems = (id: number) => {
    if (isViewOnly) {
      return;
    }
    setSelectedItems((prevSelectedItems) => {
      const newSelectedItems = prevSelectedItems.includes(id)
        ? prevSelectedItems.filter((item) => item !== id)
        : [...prevSelectedItems, id];
      updateSelectedVCSuppliers(newSelectedItems);

      return newSelectedItems;
    });
  };

  const addSuppliers = () => {
    if (isViewOnly) {
      return;
    }

    if (!recordId || selectedVCSuppliers.length === 0) {
      setIsOpenModal(false);
      return;
    }

    saveSelectedSupplier({
      maId: Number(recordId),
      data: selectedVCSuppliers,
      valueChainMapId,
    }).unwrap().then(() => {
      toast.success(i18n.t('notifications.saveSelectedSuppliers'));
      setIsOpenModal(false);
      setOffsetMain(0);
    }).catch(() => {
      toast.error(i18n.t('notifications.errorSaveSelectedSuppliers'));
    });
  };

  const closeModal = () => {
    setIsOpenModal(false);
    setSelectedItems([]);
  };

  const onOpenCreateModal = () => {
    if (isViewOnly) {
      return;
    }
    setIsCreateModalOpen(!isCreateModalOpen);
    setIsOpenModal(false);
  };

  const onDeleteItem = (id: number) => {
    if (isViewOnly) {
      return;
    }
    deleteMaterialityStakeholder({ id, maId: recordId }).then(() => {
      toast.success(i18n.t('notifications.deleteSelectedSuppliers'));
      setListSuppliers((prev) => {
        const filtered = prev.filter((item) => item.id !== id);
        if (filtered.length === 0) {
          updateSection({
            maId: recordId,
            type: SECTION_TYPE.STAKEHOLDER_SUPPLIERS,
            valueChainMapId,
            data: {
              isActive: true,
              status: STATUS_SECTIONS.IN_PROGRESS,
            },
          });
        }
        return filtered;
      });
    }).catch(() => {
      toast.error(i18n.t('notifications.errorDeleteSelectedSuppliers'));
    });
  };

  const onSearchChange = (value: string) => {
    if (value === search) {
      return;
    }

    setSearch(value);
    setOffset(0);
    setVCSuppliers([]);
    setHasMoreVCSuppliers(false);
  };

  const setUnSelectedItems = () => {
    setSelectedItems([]);
  };

  const loading = isFetchingSuppliersList || isGetNaceCodeTreeRecords || isLoadingSingleMA;

  const vcmName = singleMA?.data?.valueChainMaps?.find((item) => item.id === valueChainMapId)?.name;

  return (
    <MaterialityAssessmentSupplierView
      isEmptyList={selectedVCSuppliers?.length === 0}
      isEmptyMainList={listSuppliers.length === 0}
      loading={loading}
      goToNextMaterialityAssessment={goToNextMaterialityAssessment}
      goBack={goBack}
      addStakeholder={addStakeholder}
      isOpenModal={isOpenModal}
      addSuppliers={addSuppliers}
      closeModal={closeModal}
      selectedItems={selectedItems}
      setSelectedItems={onSelectedItems}
      onOpenCreateModal={onOpenCreateModal}
      isCreateModalOpen={isCreateModalOpen}
      vcSuppliers={vcSuppliers}
      suppliersList={listSuppliers || []}
      onDeleteItem={onDeleteItem}
      hasMore={hasMore}
      loadMoreSuppliers={loadMoreSuppliers}
      naceCodeTreeData={naceCodeTreeData}
      search={search}
      onSearchChange={onSearchChange}
      setUnSelectedItems={setUnSelectedItems}
      vcmName={vcmName || ''}
      loadMoreVCSuppliers={loadMoreVCSuppliers}
      hasMoreVCSuppliers={hasMoreVCSuppliers}
      isViewOnly={isViewOnly}
    />
  );
}

export default MaterialityAssessmentSupplier;
